import { Icon } from '@iconify/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, type StaffRow, api, fmtRelative } from '../api';
import { getLang, t } from '../i18n';
import { get, invalidate } from '../query';
import { ConfirmModal } from './Modal';
import { toastError, toastUndo } from './Toasts';
import { Sheet } from './ui/Sheet';
import { SkeletonScreen } from './ui/Skeleton';

// The house rules, for the people who actually follow them.
//
// `GET /v1/memory` has always been open to every member of the household —
// "never use vinegar on the marble" is worthless if the housekeeper cannot
// read it — but the only screen that rendered it sat behind RequireOwner, so
// staff had no way to see a single rule. Writing stays a permission
// (`assign_work`), which is how the administrative PA keeps the rules for the
// rest of the staff without becoming a second owner.
//
// One screen for both roles, mounted at /memory for the owner and /rules for
// staff. It replaced the owner-only OwnerMemory, which was the last screen
// still hardcoded in Hebrew and never offered the `pinned` flag the API has
// had since day one.

export interface MemoryEntry {
  id: string;
  category: string;
  title: string;
  body: string;
  pinned: boolean;
  /** The number to tap. Set on contacts; null on an ordinary rule. */
  phone?: string | null;
  /**
   * Who may see this entry. Empty (the default) means everyone in the
   * household. The server filters the list, so a viewer never receives an
   * entry they are not on — this field is only here so the person editing
   * can see and change who it went to.
   */
  visibleToUserIds?: string[];
  /** 'observed' = drafted by the brain from a pattern, not typed by anyone —
   *  shown with a badge so it never passes as something the owner wrote. */
  provenance?: string;
  /** Past this, the rule stops being read as live — by every screen and by
   *  the model's own context. Null/undefined = never lapses. */
  expiresAt?: string | null;
  createdAt: string;
  updatedAt?: string;
  /**
   * Per-language copy of the rule, filled by the translator a second or two
   * after it is written. The owner types in his language; the housekeeper
   * reads in hers. Empty on a rule saved before this existed, and while the
   * translator is still working — hence the fall back to the source below.
   */
  translations?: Partial<Record<string, { title?: string; body?: string }>>;
}

/** What this viewer should read, falling back to what the author typed. */
function readable(entry: MemoryEntry): { title: string; body: string } {
  const slot = entry.translations?.[getLang()];
  return {
    title: slot?.title?.trim() || entry.title,
    body: slot?.body?.trim() || entry.body,
  };
}

/** The 8 categories in `packages/schemas/src/memory.ts`, in the order the owner sees them. */
export const MEMORY_CATEGORIES = [
  'shopping_preferences',
  'cleaning_standards',
  'car_rules',
  'guest_handling',
  'deliveries',
  'emergency_procedures',
  'contacts',
  'other',
] as const;

/**
 * Source strings for the category names. Kept as plain English keys so they
 * go through the same `t()` overlay as the rest of the app — the person
 * reading the rules is usually the one whose language is not Hebrew.
 */
const CATEGORY_LABEL: Record<string, string> = {
  shopping_preferences: 'Shopping',
  cleaning_standards: 'Cleaning',
  car_rules: 'Car',
  guest_handling: 'Guests',
  deliveries: 'Deliveries',
  emergency_procedures: 'Emergency',
  contacts: 'Contacts',
  other: 'Other',
};

export function categoryLabel(category: string): string {
  const source = CATEGORY_LABEL[category];
  return source ? t(source) : category;
}

/** His chip icons, one per category the schema already defines. */
const CATEGORY_ICON: Record<string, string> = {
  shopping_preferences: 'ph:shopping-cart-bold',
  cleaning_standards: 'ph:broom-bold',
  car_rules: 'ph:car-bold',
  guest_handling: 'ph:users-three-bold',
  deliveries: 'ph:package-bold',
  emergency_procedures: 'ph:first-aid-kit-bold',
  contacts: 'ph:phone-call-bold',
  other: 'ph:note-bold',
};

export function HouseRules() {
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<MemoryEntry | 'new' | null>(null);
  const [reading, setReading] = useState<MemoryEntry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MemoryEntry | null>(null);
  const [query, setQuery] = useState('');

  const rulesQ = useQuery({ queryKey: ['/v1/memory'], queryFn: get<{ items: MemoryEntry[] }> });
  const items = rulesQ.data?.items ?? [];

  // Whether to *offer* writing. The server enforces it on every write
  // regardless, so a stale answer here can only cost a 403 — never a rule this
  // person wasn't allowed to change.
  const meQ = useQuery({
    queryKey: ['/v1/me'],
    queryFn: get<{ permissions: { assign_work: boolean } }>,
  });
  const canWrite = meQ.data?.permissions.assign_work ?? false;

  // The filter lives in the URL so "Emergency contacts" on the More screen can
  // be a link to this screen rather than a second screen showing the same rows.
  const filter = params.get('category');
  function setFilter(c: string | null) {
    const next = new URLSearchParams(params);
    if (c) next.set('category', c);
    else next.delete('category');
    setParams(next, { replace: true });
  }

  const pin = useMutation({
    mutationFn: ({ entry, pinned }: { entry: MemoryEntry; pinned: boolean }) =>
      api(`/v1/memory/${entry.id}`, { method: 'PATCH', body: JSON.stringify({ pinned }) }),
    onSuccess: (_r, { entry, pinned }) => {
      // Pinning is reversible and cheap, so it says so rather than asking.
      toastUndo(pinned ? t('Pinned') : t('Unpinned'), () => pin.mutate({ entry, pinned: !pinned }));
    },
    onError: (e) =>
      // PIN_CAP is the only failure a person can act on, and it arrives with a
      // machine code — say the actionable sentence instead.
      toastError(
        e instanceof ApiError && e.code === 'PIN_CAP'
          ? new Error(t('Only 8 rules can be pinned. Unpin one first.'))
          : e,
        t('Saving failed'),
      ),
    onSettled: () => invalidate('/v1/memory'),
  });

  const remove = useMutation({
    mutationFn: (entry: MemoryEntry) => api(`/v1/memory/${entry.id}`, { method: 'DELETE' }),
    onError: (e) => toastError(e, t('Saving failed')),
    onSettled: () => invalidate('/v1/memory'),
  });

  // Only offer a chip for a category that actually has a rule in it — an
  // empty filter that yields an empty screen reads like a broken app.
  const usedCategories = useMemo(() => {
    const present = new Set(items.map((m) => m.category));
    return MEMORY_CATEGORIES.filter((c) => present.has(c));
  }, [items]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((m) => {
      if (filter && m.category !== filter) return false;
      if (!q) return true;
      const shown = readable(m);
      return (
        shown.title.toLowerCase().includes(q) ||
        shown.body.toLowerCase().includes(q) ||
        (m.phone ?? '').includes(q)
      );
    });
  }, [items, filter, query]);

  // The API already returns pinned first, then most-recently-updated. Split
  // rather than re-sort so the two lists stay in the server's order.
  const pinned = visible.filter((m) => m.pinned);
  const rest = visible.filter((m) => !m.pinned);

  return (
    <div className="mx-auto w-full max-w-lg pb-24 lg:max-w-[800px]">
      <header className="sticky top-0 z-30 -mx-4 -mt-6 bg-background/95 px-4 pb-4 pt-6 backdrop-blur-xl">
        <div className="mb-6 flex items-center justify-between gap-3">
          <h1 className="font-heading text-[32px] font-semibold leading-none tracking-tight">
            {t('House rules')}
          </h1>
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditing('new')}
              aria-label={t('Add a rule')}
              className="flex h-12 w-12 items-center justify-center rounded-[20px] bg-foreground text-background shadow-lg transition-all active:scale-95"
            >
              <Icon icon="ph:plus-bold" className="text-2xl" />
            </button>
          )}
        </div>

        <div className="relative mb-6">
          <Icon
            icon="solar:magnifer-bold"
            className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-xl text-muted-foreground opacity-50"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search the rules…')}
            aria-label={t('Search the rules…')}
            dir="auto"
            className="h-14 w-full rounded-[20px] border border-border bg-card pe-4 ps-12 text-[15px] font-medium shadow-[0_4px_12px_rgba(0,0,0,0.02)] placeholder:text-muted-foreground/50 focus:border-primary/30 focus:outline-none"
          />
        </div>

        {usedCategories.length > 1 && (
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2">
            <Chip active={filter === null} onClick={() => setFilter(null)}>
              {t('All')}
            </Chip>
            {usedCategories.map((c) => (
              <Chip
                key={c}
                active={filter === c}
                onClick={() => setFilter(c)}
                icon={CATEGORY_ICON[c]}
              >
                {categoryLabel(c)}
              </Chip>
            ))}
          </div>
        )}
      </header>

      <main className="space-y-10 py-8">
        {rulesQ.isPending && <SkeletonScreen rows={4} />}

        {rulesQ.isError && (
          <div className="rounded-3xl border border-urgent/25 bg-urgent/10 p-5 text-sm font-semibold text-urgent">
            {rulesQ.error instanceof ApiError ? rulesQ.error.message : t('Loading failed')}
            <button
              type="button"
              onClick={() => void rulesQ.refetch()}
              className="mt-3 block rounded-2xl bg-urgent/10 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors active:bg-urgent/20"
            >
              {t('Try again')}
            </button>
          </div>
        )}

        {!rulesQ.isPending && !rulesQ.isError && items.length === 0 && (
          <div className="rounded-[2.5rem] border border-border bg-card p-8 text-center text-sm font-bold text-muted-foreground">
            {t('No house rules yet.')}
          </div>
        )}
        {!rulesQ.isPending && !rulesQ.isError && items.length > 0 && visible.length === 0 && (
          <div className="rounded-[2.5rem] border border-border bg-card p-8 text-center text-sm font-bold text-muted-foreground">
            {t('Nothing matches that.')}
          </div>
        )}

        {pinned.length > 0 && (
          <section className="space-y-4">
            <h2 className="px-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t('Always remember')}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {pinned.map((m) => (
                <PinnedCard key={m.id} entry={m} onOpen={() => setReading(m)} />
              ))}
            </div>
          </section>
        )}

        {rest.length > 0 && (
          <section className="space-y-4">
            <h2 className="px-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {pinned.length > 0 ? t('The rest') : t('All rules')}
            </h2>
            <div className="flex flex-col gap-3">
              {rest.map((m) => (
                <RuleRow key={m.id} entry={m} onOpen={() => setReading(m)} />
              ))}
            </div>
          </section>
        )}
      </main>

      {reading && (
        <RuleSheetView
          entry={reading}
          canWrite={canWrite}
          onClose={() => setReading(null)}
          onEdit={() => {
            const e = reading;
            setReading(null);
            setEditing(e);
          }}
          onDelete={() => {
            const e = reading;
            setReading(null);
            setConfirmDelete(e);
          }}
          onTogglePin={() => {
            pin.mutate({ entry: reading, pinned: !reading.pinned });
            setReading(null);
          }}
        />
      )}

      {editing && (
        <RuleSheet
          entry={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate('/v1/memory');
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title={t('Delete this rule?')}
          body={readable(confirmDelete).title}
          confirmLabel={t('Delete')}
          destructive
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => remove.mutate(confirmDelete)}
        />
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-[12px] tracking-tight transition-colors ${
        active
          ? 'bg-foreground font-bold text-background'
          : 'border border-border bg-card font-semibold text-foreground shadow-sm'
      }`}
    >
      {icon && <Icon icon={icon} className={active ? 'text-base' : 'text-base text-primary'} />}
      {children}
    </button>
  );
}

/** The eight things everyone is meant to know, as tiles rather than a list. */
function PinnedCard({ entry, onOpen }: { entry: MemoryEntry; onOpen: () => void }) {
  const shown = readable(entry);
  const audience =
    entry.visibleToUserIds && entry.visibleToUserIds.length > 0
      ? t('{{n}} people').replace('{{n}}', String(entry.visibleToUserIds.length))
      : t('Everyone');
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative flex aspect-square flex-col gap-4 rounded-[24px] border border-border bg-card p-5 text-start shadow-[0_8px_30px_rgba(0,0,0,0.02)] transition-transform active:scale-[0.98]"
    >
      <span className="absolute end-4 top-4 text-primary">
        <Icon icon="solar:pin-bold" className="text-lg" />
      </span>
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/5">
        <Icon
          icon={CATEGORY_ICON[entry.category] ?? CATEGORY_ICON.other!}
          className="text-xl text-primary"
        />
      </div>
      <div className="mt-auto flex min-w-0 flex-col gap-1.5">
        <h3 className="font-heading text-[15px] font-semibold leading-tight" dir="auto">
          {shown.title}
        </h3>
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground opacity-60">
          {audience} · {fmtRelative(entry.updatedAt ?? entry.createdAt)}
        </span>
      </div>
    </button>
  );
}

/** "The brain drafted this from a pattern, not a person" — never rendered
 *  without this next to it, so it never passes as owner-written. */
function ObservedBadge() {
  return (
    <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-primary normal-case tracking-normal">
      <Icon icon="ph:sparkle-fill" className="text-[10px]" />
      {t('Observed')}
    </span>
  );
}

function RuleRow({ entry, onOpen }: { entry: MemoryEntry; onOpen: () => void }) {
  const shown = readable(entry);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-4 rounded-[24px] border border-border bg-card p-4 text-start shadow-[0_4px_20px_rgba(0,0,0,0.01)] transition-all active:scale-[0.99]"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] border border-border bg-background shadow-sm">
        <Icon
          icon={CATEGORY_ICON[entry.category] ?? CATEGORY_ICON.other!}
          className="text-xl text-primary"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <h3 className="truncate text-[15px] font-bold" dir="auto">
          {shown.title}
        </h3>
        <span className="mt-0.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground opacity-60">
          {entry.provenance === 'observed' && <ObservedBadge />}
          {categoryLabel(entry.category)} · {fmtRelative(entry.updatedAt ?? entry.createdAt)}
        </span>
      </div>
      <Icon
        icon="solar:alt-arrow-right-bold"
        className="shrink-0 text-xl text-[#C4C4C2] rtl:rotate-180"
      />
    </button>
  );
}

/**
 * The rule itself.
 *
 * His list rows carry only a title, which is the right call for eighty rules
 * and the wrong one if the sentence is never reachable — so the row opens the
 * rule, with the phone number as the button it always was.
 */
function RuleSheetView({
  entry,
  canWrite,
  onClose,
  onEdit,
  onDelete,
  onTogglePin,
}: {
  entry: MemoryEntry;
  canWrite: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
}) {
  const shown = readable(entry);
  return (
    <Sheet title={categoryLabel(entry.category)} onClose={onClose} tall>
      {entry.provenance === 'observed' && (
        <div className="flex items-center gap-2 rounded-2xl bg-primary/10 p-3 text-xs font-semibold text-primary">
          <Icon icon="ph:sparkle-fill" className="shrink-0 text-base" />
          {t(
            'Drafted automatically from a repeated pattern — not written by anyone. Edit it into a real rule, or delete it.',
          )}
        </div>
      )}
      {/* dir="auto" — the rule may still be in the author's language (a
          translation that hasn't landed yet, or a rule older than this), and
          without it an English rule inside the Hebrew UI throws its full stop
          to the front of the line. */}
      <h3 className="font-heading text-xl font-bold tracking-tight" dir="auto">
        {shown.title}
      </h3>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80" dir="auto">
        {shown.body}
      </p>
      {entry.expiresAt && (
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          {t('Expires {{when}}').replace('{{when}}', fmtRelative(entry.expiresAt))}
        </p>
      )}

      {/* Tap to call. The whole point of keeping the plumber in here is not
          having to copy a number out of a sentence at 6am. */}
      {entry.phone && (
        <a
          href={`tel:${entry.phone.replace(/[^+\d]/g, '')}`}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 text-sm font-bold text-primary-foreground"
        >
          <Icon icon="ph:phone-call-fill" />
          {t('Call {{number}}').replace('{{number}}', entry.phone)}
        </a>
      )}

      {canWrite && entry.visibleToUserIds && entry.visibleToUserIds.length > 0 && (
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          {t('Only {{n}} people see this').replace('{{n}}', String(entry.visibleToUserIds.length))}
        </p>
      )}

      {canWrite && (
        <div className="flex items-center gap-2 border-t border-border pt-4">
          <button type="button" className="btn-ghost flex-1 text-sm" onClick={onTogglePin}>
            {entry.pinned ? t('Unpin') : t('Pin')}
          </button>
          <button type="button" className="btn-ghost flex-1 text-sm" onClick={onEdit}>
            {t('Edit')}
          </button>
          <button type="button" className="btn-ghost flex-1 text-sm text-urgent" onClick={onDelete}>
            {t('Delete')}
          </button>
        </div>
      )}
    </Sheet>
  );
}

/**
 * Add or edit one rule. `entry === null` means "new".
 *
 * Shared by the staff screen and the owner screen so the two can never drift
 * apart — the owner and the administrative PA are editing the same rules.
 */
export function RuleSheet({
  entry,
  onClose,
  onSaved,
  seedCategory,
}: {
  entry: MemoryEntry | null;
  onClose: () => void;
  onSaved: () => void;
  /** Prefills the category when opening on a fresh rule from a suggestion
   *  card's apply.seed — never used once `entry` is set (editing an
   *  existing rule always shows its own category). */
  seedCategory?: string;
}) {
  const [category, setCategory] = useState(
    entry?.category ?? seedCategory ?? 'shopping_preferences',
  );
  // The editor edits what they were shown. The housekeeper reads "No vinegar
  // on the marble" and taps Edit; handing her back the Hebrew original means
  // only the person who wrote a rule can ever fix it. The server records the
  // language the new words are in, so her English edit is not filed as
  // Hebrew and the owner's own slot comes back in Hebrew.
  const shown = entry ? readable(entry) : null;
  const [title, setTitle] = useState(shown?.title ?? '');
  const [body, setBody] = useState(shown?.body ?? '');
  const [pinned, setPinned] = useState(entry?.pinned ?? false);
  const [phone, setPhone] = useState(entry?.phone ?? '');
  const [audience, setAudience] = useState<string[]>(entry?.visibleToUserIds ?? []);
  // yyyy-mm-dd for the date input; empty means "never lapses", which is what
  // every rule written before expiry existed already carries.
  const [expiresOn, setExpiresOn] = useState(entry?.expiresAt?.slice(0, 10) ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Needed only to name the people in the audience picker. A failure here
  // costs the picker, not the rule — everyone is the default anyway. Shared
  // with every other screen that asks for the team.
  const staffQ = useQuery({ queryKey: ['/v1/staff'], queryFn: get<{ items: StaffRow[] }> });
  const staff = (staffQ.data?.items ?? []).filter((x) => !x.disabled_at);

  function toggleViewer(id: string) {
    setAudience((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        category,
        title,
        body,
        pinned,
        phone: phone.trim() || undefined,
        visible_to_user_ids: audience,
        // Explicit null clears an existing expiry on an edit; a fresh entry
        // simply omits it, which the API already treats as "never lapses".
        expires_at: expiresOn
          ? new Date(`${expiresOn}T00:00:00.000Z`).toISOString()
          : entry
            ? null
            : undefined,
      };
      if (entry) {
        await api(`/v1/memory/${entry.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } else {
        await api('/v1/memory', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (e) {
      // The pin cap is the one error a person can actually act on, so it gets
      // a sentence instead of the raw "Pin capacity is 8; unpin one first."
      setErr(
        e instanceof ApiError
          ? e.code === 'PIN_CAP'
            ? t('Only 8 rules can be pinned. Unpin one first.')
            : e.message
          : t('Saving failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={entry ? t('Edit rule') : t('New house rule')} onClose={onClose} onSubmit={submit}>
      <select
        className="input"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        aria-label={t('Category')}
      >
        {MEMORY_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {categoryLabel(c)}
          </option>
        ))}
      </select>
      {/* dir="auto" on both: the author may type Hebrew, Urdu, Tagalog or
          English regardless of which UI language they are in. */}
      <input
        className="input"
        dir="auto"
        placeholder={t('Short title')}
        aria-label={t('Short title')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <textarea
        className="input min-h-[120px]"
        dir="auto"
        placeholder={t('What the house needs to remember')}
        aria-label={t('What the house needs to remember')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
      />
      <input
        className="input"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        enterKeyHint="done"
        dir="ltr"
        placeholder={t('Phone (optional) — makes this tap-to-call')}
        aria-label={t('Phone (optional) — makes this tap-to-call')}
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        maxLength={40}
      />
      <label className="flex items-center gap-2 text-sm text-foreground/80">
        <input
          type="checkbox"
          className="h-4 w-4 accent-primary"
          checked={pinned}
          onChange={(e) => setPinned(e.target.checked)}
        />
        {t('Pin to the top — everyone sees this first')}
      </label>

      <label className="flex flex-col gap-1 text-sm text-foreground/80">
        {t('Expires on (optional)')}
        <input
          type="date"
          className="input"
          value={expiresOn}
          onChange={(e) => setExpiresOn(e.target.value)}
          aria-label={t('Expires on (optional)')}
        />
      </label>

      {/* Who sees it. Everyone by default — a rule nobody can read protects
          nothing, so the open answer is the one you get for free. Naming
          people is for the entries that belong to one person's job. */}
      {staff.length > 0 && (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="text-sm text-foreground/80">{t('Who sees this?')}</div>
          <button
            type="button"
            onClick={() => setAudience([])}
            className={`w-full rounded-lg border px-3 py-2 text-start text-sm transition-colors ${
              audience.length === 0
                ? 'border-primary bg-muted text-foreground'
                : 'border-border text-foreground/80 hover:border-muted-foreground'
            }`}
          >
            {t('Everyone in the house')}
          </button>
          {staff.map((person) => (
            <label
              key={person.id}
              className="flex cursor-pointer items-center gap-2 px-1 text-sm text-foreground/80"
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={audience.includes(person.id)}
                onChange={() => toggleViewer(person.id)}
              />
              <span dir="auto">{person.full_name}</span>
            </label>
          ))}
          <p className="text-xs text-muted-foreground">
            {t('You always see everything you wrote.')}
          </p>
        </div>
      )}
      {err && <div className="text-sm text-danger">{err}</div>}
      <div className="flex gap-2">
        <button type="submit" className="btn-primary flex-1" disabled={busy}>
          {t('Save')}
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          {t('Cancel')}
        </button>
      </div>
    </Sheet>
  );
}
